import jsPDF from 'jspdf';
import { calculateInvoiceTotal } from './api';
import { api } from './api';

let LOGO_CACHE = null;
const loadLogoBase64 = async () => {
  if (LOGO_CACHE) return LOGO_CACHE;
  try {
    const res = await fetch('/assets/wolf-logo.png');
    const blob = await res.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => { LOGO_CACHE = reader.result; resolve(LOGO_CACHE); };
      reader.readAsDataURL(blob);
    });
  } catch (e) { return null; }
};

// Plain Indian number — no currency prefix, no decimals
const inrPlain = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yy = dt.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

// Fallback Wolf Media contact info if company fields not populated
const WM_FALLBACK = {
  address_lines: [
    'Reg off: Atrium, A-5, the Surya Hotel, New Friends Colony,',
    'New Delhi-110025',
    'Branch off: 68, Jasola, Pocket-2, New Delhi-110025',
  ],
  phone: '8252625040',
  email: 'sales@wolfmediamarketing.com',
};

const parseAddressLines = (address) => {
  if (!address) return WM_FALLBACK.address_lines;
  // Split on newlines or sentence boundaries
  return address.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
};

export const generateInvoicePDF = async (invoice, opts = {}) => {
  const logo = await loadLogoBase64();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;

  const ink = [10, 10, 10];
  const darkBg = [44, 44, 44];
  const subtle = [110, 110, 110];
  const borderGrey = [220, 220, 220];

  // Fetch bank accounts for this invoice's company (payment methods)
  let bankAccounts = [];
  try {
    if (invoice.company_id) {
      const r = await api.get('/bank-accounts', { params: { company_id: invoice.company_id } });
      bankAccounts = r.data || [];
    }
  } catch (e) { bankAccounts = []; }

  // ===== TOP HEADER =====
  // Sender company name (top-left, big bold)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...ink);
  const senderName = invoice.company?.full_name || invoice.company?.name || 'Wolf Media';
  doc.text(senderName, M, 70);

  // Invoice meta labels + values (small caps, below sender)
  const metaY = 100;
  const metaValY = 116;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...subtle);
  doc.text('INVOICE DATE', M, metaY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...ink);
  doc.text(fmtDate(invoice.date), M, metaValY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...subtle);
  doc.text('INVOICE NO.', M + 160, metaY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...ink);
  doc.text(invoice.invoice_no || '', M + 160, metaValY);

  if (invoice.due_date) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...subtle);
    doc.text('DUE DATE', M + 320, metaY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...ink);
    doc.text(fmtDate(invoice.due_date), M + 320, metaValY);
  }

  // WOLF MEDIA logo top-right
  if (logo) {
    try {
      doc.setFillColor(0, 0, 0);
      doc.rect(W - M - 110, 36, 110, 80, 'F');
      doc.addImage(logo, 'PNG', W - M - 102, 44, 94, 64, undefined, 'FAST');
    } catch (e) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(26);
      doc.setTextColor(230, 0, 41);
      doc.text('WOLF', W - M, 70, { align: 'right' });
      doc.setTextColor(...ink);
      doc.setFontSize(20);
      doc.text('MEDIA', W - M, 96, { align: 'right' });
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(230, 0, 41);
    doc.text('WOLF', W - M, 70, { align: 'right' });
    doc.setTextColor(...ink);
    doc.setFontSize(20);
    doc.text('MEDIA', W - M, 96, { align: 'right' });
  }

  // Thick dark separator bar below header
  let y = 140;
  doc.setFillColor(...darkBg);
  doc.rect(M, y, W - 2 * M, 4, 'F');

  // ===== RECIPIENT DETAILS =====
  y += 24;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...ink);
  doc.text('RECIPIENT DETAILS', M, y);

  y += 8;
  doc.setDrawColor(...borderGrey);
  doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y);

  y += 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...subtle);
  doc.text('RECIPIENT NAME', M, y);

  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...ink);
  doc.text(invoice.client?.name || '', M, y);

  if (invoice.client?.address) {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    const addrLines = doc.splitTextToSize(invoice.client.address, (W - 2 * M) * 0.55);
    doc.text(addrLines, M, y);
    y += addrLines.length * 11;
  }
  if (invoice.client?.gstin) {
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    doc.text(`GSTIN: ${invoice.client.gstin}`, M, y);
  }

  // Service + period right side
  if (invoice.service || invoice.period) {
    let ry = 198;
    if (invoice.service) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...subtle);
      doc.text('SERVICE', W - M - 200, ry);
      ry += 14;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...ink);
      doc.text(invoice.service, W - M - 200, ry);
    }
    if (invoice.period) {
      ry += 18;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(...subtle);
      doc.text('PERIOD', W - M - 200, ry);
      ry += 14;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...ink);
      doc.text(invoice.period, W - M - 200, ry);
    }
  }

  // ===== ITEMS TABLE =====
  y = Math.max(y + 36, 290);
  const tableX = M;
  const tableW = W - 2 * M;

  // Column positions
  const colItems = M + 12;
  const colDesc = M + 130;
  const colAmount = M + tableW * 0.65;
  const colTotal = W - M - 12;

  // Dark header bar
  doc.setFillColor(...darkBg);
  doc.rect(tableX, y, tableW, 26, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('ITEMS', colItems, y + 17);
  doc.text('DESCRIPTION', colDesc, y + 17);
  doc.text('AMOUNT', colAmount, y + 17);
  doc.text('TOTAL', colTotal, y + 17, { align: 'right' });

  y += 26;

  // Items
  const items = invoice.items || [];
  doc.setTextColor(...ink);

  items.forEach((item, idx) => {
    const rowTop = y;
    const label = invoice.service ? invoice.service : (item.title || `Item ${idx + 1}`);
    const descLines = doc.splitTextToSize(item.desc || '', colAmount - colDesc - 14);
    const rowH = Math.max(34, descLines.length * 12 + 22);

    // Row content
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, colItems, rowTop + 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(descLines, colDesc, rowTop + 20);

    doc.text(inrPlain(item.amt), colAmount, rowTop + 20);

    doc.setFont('helvetica', 'bold');
    doc.text(inrPlain(item.amt), colTotal, rowTop + 20, { align: 'right' });

    y = rowTop + rowH;

    // Thin divider between rows
    doc.setDrawColor(...borderGrey);
    doc.setLineWidth(0.4);
    doc.line(tableX, y, tableX + tableW, y);
  });

  // Totals
  const { subtotal, gst, tds, total } = calculateInvoiceTotal(invoice);
  if (gst > 0 || tds > 0) {
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    doc.text('Subtotal', colAmount, y);
    doc.setTextColor(...ink);
    doc.text(inrPlain(subtotal), colTotal, y, { align: 'right' });
    if (gst > 0) {
      y += 14;
      doc.setTextColor(...subtle);
      doc.text(`GST (${invoice.gst_percent}%)`, colAmount, y);
      doc.setTextColor(...ink);
      doc.text(inrPlain(gst), colTotal, y, { align: 'right' });
    }
    if (tds > 0) {
      y += 14;
      doc.setTextColor(...subtle);
      doc.text(`TDS (${invoice.tds_percent}%)`, colAmount, y);
      doc.setTextColor(...ink);
      doc.text('- ' + inrPlain(tds), colTotal, y, { align: 'right' });
    }
    y += 8;
  }

  // TOTAL row (bold, with top thick line, no fill — matches artifact)
  y += 12;
  doc.setDrawColor(...darkBg);
  doc.setLineWidth(1.5);
  doc.line(tableX, y - 4, tableX + tableW, y - 4);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...ink);
  doc.text('TOTAL', colItems, y + 14);
  doc.text(inrPlain(total), colTotal, y + 14, { align: 'right' });

  y += 22;
  doc.setDrawColor(...darkBg);
  doc.setLineWidth(1.5);
  doc.line(tableX, y, tableX + tableW, y);

  // ===== PAYMENT PROCESS REQUEST =====
  y += 36;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...ink);
  doc.text('PAYMENT PROCESS REQUEST', M, y);

  y += 8;
  doc.setDrawColor(...borderGrey);
  doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y);

  // Build payment methods rows from bank accounts
  const methods = [];
  if (bankAccounts && bankAccounts.length > 0) {
    const split = total / bankAccounts.length;
    bankAccounts.forEach(b => {
      const lines = [];
      if (b.bank_name) lines.push(`BANK: ${b.bank_name}`);
      if (b.label) lines.push(`A/C HOLDER: ${b.label}`);
      if (b.account_number_last4) lines.push(`ACCOUNT NUMBER: XXXX${b.account_number_last4}`);
      if (b.account_type) lines.push(`TYPE: ${b.account_type}`);
      methods.push({ amount: split, detailLines: lines });
    });
  } else if (invoice.company?.bank_details) {
    // Fallback to single method from company.bank_details text
    const detailLines = invoice.company.bank_details.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    methods.push({ amount: total, detailLines });
  }

  // Payment table
  y += 12;
  const pAmountColX = M + 12;
  const pMethodColX = M + 160;
  const pAmountColW = 130;

  // Header row
  doc.setFillColor(...darkBg);
  doc.rect(M, y, W - 2 * M, 22, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('AMOUNT', pAmountColX, y + 15);
  doc.text('METHOD', pMethodColX, y + 15);
  y += 22;

  // Vertical divider line between Amount / Method columns
  const tableTopY = y;

  methods.forEach((m, idx) => {
    const rowTop = y;
    const rowH = Math.max(36, m.detailLines.length * 12 + 18);

    // Amount cell
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...ink);
    doc.text(inrPlain(m.amount), pAmountColX, rowTop + 22);

    // Method cell
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...ink);
    let mLineY = rowTop + 16;
    m.detailLines.forEach(line => {
      doc.text(line, pMethodColX, mLineY);
      mLineY += 12;
    });

    y = rowTop + rowH;

    // Bottom divider for each row
    if (idx < methods.length - 1) {
      doc.setDrawColor(...borderGrey);
      doc.setLineWidth(0.4);
      doc.line(M, y, W - M, y);
    }
  });

  // Outer box + vertical divider for payment table
  const tableBottomY = y;
  doc.setDrawColor(...borderGrey);
  doc.setLineWidth(0.6);
  doc.rect(M, tableTopY - 22, W - 2 * M, tableBottomY - tableTopY + 22);
  doc.line(pMethodColX - 14, tableTopY, pMethodColX - 14, tableBottomY);

  if (methods.length === 0) {
    y += 10;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    doc.text('No bank accounts configured for this company.', M, y);
  }

  // Notes
  if (invoice.notes) {
    y += 24;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...ink);
    doc.text('NOTES', M, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...subtle);
    const notesLines = doc.splitTextToSize(invoice.notes, W - 2 * M);
    doc.text(notesLines, M, y);
  }

  // ===== FOOTER =====
  const footerY = H - 90;
  doc.setDrawColor(...borderGrey);
  doc.setLineWidth(0.6);
  doc.line(M, footerY, W - M, footerY);

  // Left: addresses
  const addressLines = parseAddressLines(invoice.company?.address);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...ink);
  let aY = footerY + 16;
  addressLines.slice(0, 4).forEach(line => {
    doc.text(line, M, aY);
    aY += 12;
  });

  // Right: Contact Us
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text('Contact Us', W - M, footerY + 16, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...ink);
  const phone = invoice.company?.phone || WM_FALLBACK.phone;
  const email = invoice.company?.email || WM_FALLBACK.email;
  doc.text(phone, W - M, footerY + 32, { align: 'right' });
  doc.text(email, W - M, footerY + 46, { align: 'right' });

  if (invoice.company?.gstin) {
    doc.setTextColor(...subtle);
    doc.setFontSize(8);
    doc.text(`GSTIN: ${invoice.company.gstin}`, W - M, footerY + 62, { align: 'right' });
  }

  if (!opts.skipSave) {
    doc.save(`${invoice.invoice_no.replace(/\//g, '_')}.pdf`);
  }
  return doc;
};

export const generateInvoicePDFBase64 = async (invoice) => {
  const doc = await generateInvoicePDF(invoice);
  return doc.output('datauristring').split(',')[1];
};

export const generateInvoicePDFBlobUrl = async (invoice) => {
  // Render PDF without triggering download — return a blob URL for iframe preview
  const doc = await generateInvoicePDF(invoice, { skipSave: true });
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
};
